import { useCallback, useMemo, useState } from 'react';
import pluralize from 'pluralize';
import toast from 'react-hot-toast';

import { trackEvent } from 'helpers/analytics';
import { useGetListTreeQuery, useBulkMoveListsToFolderMutation } from 'services/listsTree';
import { flattenFolderOptions } from 'services/listsTree/flattenFolderOptions';
import useOnClickOutside from 'hooks/useClickOutside';

/**
 * State + mutation glue for the "Move to folder" bulk action: popover
 * open/close, search, folder picker options, and the move request with
 * its toast + analytics.
 */
export const useBulkMoveToFolder = ({ selectedRows, networkId, onMoved }) => {
  const [showCard, setShowCard] = useState(false);
  const [search, setSearch] = useState('');

  const { data: tree, isFetching } = useGetListTreeQuery({ networkId });
  const [bulkMove, { isLoading }] = useBulkMoveListsToFolderMutation();

  const allOptions = useMemo(() => flattenFolderOptions(tree), [tree]);
  const options = useMemo(() => {
    if (!search) return allOptions;
    const needle = search.toLowerCase();
    return allOptions.filter((item) => item.label.toLowerCase().includes(needle));
  }, [allOptions, search]);

  const close = useCallback(() => {
    setShowCard(false);
    setSearch('');
  }, []);

  const containerRef = useOnClickOutside(close);

  const toggle = useCallback(() => setShowCard((prev) => !prev), []);

  const onSelect = useCallback(
    async ({ value }) => {
      const result = await bulkMove({ listIds: selectedRows, parentId: value.id, networkId });

      if (result?.error) {
        toast("Couldn't move lists, please try again", { icon: 'danger', id: 'bulk-move-error' });
        return;
      }

      close();

      const count = selectedRows.length;
      toast(`${count} ${pluralize('list', count)} moved to "${value.name}"`, {
        icon: 'success',
        id: 'bulk-move-success',
      });
      trackEvent('lists:bulk_move_to_folder', {
        collectionId: networkId,
        listIds: selectedRows,
        parentId: value.id,
        page: 'lists',
        origin: 'bulk_action',
      });
      onMoved?.({ destinationName: value.name, count });
    },
    [bulkMove, close, networkId, onMoved, selectedRows],
  );

  return {
    containerRef,
    showCard,
    close,
    toggle,
    search,
    setSearch,
    options,
    isFetching,
    isLoading,
    title: `Move ${selectedRows.length} ${pluralize('list', selectedRows.length)} to...`,
    onSelect,
  };
};
